import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getRouteState } from "@/lib/navigation";

const COVERAGE_OPTIONS = [
  { id: "full-wrap", label: "Full wrap" },
  { id: "partial-wrap", label: "Partial wrap" },
  { id: "panel-graphics", label: "Panel / door graphics" },
  { id: "decals-only", label: "Decals / cut graphics" },
  { id: "advise-me", label: "Advise me" },
];

/**
 * Vehicle graphics configurator — coverage level + quantity + notes + lamination
 * + live quote summary. State is owned by the parent page.
 */
export default function VehicleConfigurator({
  configuratorRef,
  location,
  vehicleType,
  coverage,
  setCoverage,
  quantity,
  setQuantity,
  notes,
  setNotes,
  lamination,
  setLamination,
  quoteHref,
}) {
  const coverageLabel = COVERAGE_OPTIONS.find((o) => o.id === coverage)?.label || "Advise me";

  const summaryRows = [
    { label: "Vehicle type", value: vehicleType.title },
    { label: "Coverage", value: coverageLabel },
    { label: "Vehicles", value: quantity || "1" },
    { label: "Lamination", value: lamination ? "Please advise" : "Not requested" },
  ];

  return (
    <section
      ref={configuratorRef}
      className="mt-16 rounded-sm border border-brand-border bg-brand-surface/35 p-6 md:p-8"
      data-testid="vehicle-configurator-section"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-8 items-start">
        <div>
          <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-brand-gold">
            Vehicle graphics configurator
          </p>
          <h2 className="mt-3 font-heading text-3xl tracking-tight text-brand-text">
            Tell us what you need
          </h2>

          {/* Coverage */}
          <div className="mt-6">
            <span className="block text-[11px] tracking-wide uppercase text-brand-text-secondary/70 mb-3">
              Coverage level
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="vehicle-coverage-group">
              {COVERAGE_OPTIONS.map((option) => {
                const active = coverage === option.id;
                const cls = active
                  ? "border-brand-gold bg-brand-gold/10 text-brand-gold"
                  : "border-brand-border text-brand-text-secondary hover:text-brand-text";
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setCoverage(option.id)}
                    className={`rounded-sm border px-4 py-3 text-left text-[12px] font-medium transition-colors duration-200 ${cls}`}
                    data-testid={`vehicle-coverage-${option.id}`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quantity */}
          <div className="mt-6" data-testid="vehicle-quantity-group">
            <label>
              <span className="block text-[11px] tracking-wide uppercase text-brand-text-secondary/70 mb-2">
                Number of vehicles
              </span>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full bg-transparent border-b border-brand-border focus:border-brand-gold text-brand-text py-3 outline-none transition-colors duration-300 text-[15px]"
                data-testid="vehicle-quantity-input"
              />
            </label>
          </div>

          {/* Notes */}
          <div className="mt-6" data-testid="vehicle-notes-group">
            <label>
              <span className="block text-[11px] tracking-wide uppercase text-brand-text-secondary/70 mb-2">
                Additional notes <span className="normal-case text-brand-text-secondary/40">(optional)</span>
              </span>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Vehicle make/model, areas to cover, artwork or logo details..."
                className="w-full bg-transparent border-b border-brand-border focus:border-brand-gold text-brand-text py-3 outline-none transition-colors duration-300 text-[14px] resize-none placeholder:text-brand-text-secondary/25"
                data-testid="vehicle-notes-input"
              />
            </label>
          </div>

          {/* Lamination */}
          <label className="mt-6 flex items-start gap-3" data-testid="vehicle-lamination-group">
            <input
              type="checkbox"
              checked={lamination}
              onChange={(e) => setLamination(e.target.checked)}
              className="mt-1 h-4 w-4 accent-[#CBA052]"
              data-testid="vehicle-lamination-checkbox"
            />
            <span>
              <span className="block text-[13px] font-medium text-brand-text">
                Discuss lamination / extra protection if suitable
              </span>
              <span className="mt-1 block text-[12px] leading-relaxed text-brand-text-secondary/55">
                Relevant where graphics need to handle regular cleaning, road exposure, or where finish durability matters.
              </span>
            </span>
          </label>
        </div>

        {/* Quote summary panel */}
        <div className="rounded-sm border border-white/[0.06] bg-brand-bg/70 p-6" data-testid="vehicle-quote-summary">
          <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-brand-gold">
            Quote Summary
          </p>
          <div className="mt-5 space-y-3">
            {summaryRows.map((item) => (
              <div
                key={item.label}
                className="flex items-start justify-between gap-4 border-b border-white/[0.05] pb-3 last:border-b-0 last:pb-0"
                data-testid={`vehicle-summary-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <span className="text-[11px] uppercase tracking-[0.16em] text-brand-text-secondary/40">
                  {item.label}
                </span>
                <span className="text-[13px] font-medium text-brand-text text-right">{item.value}</span>
              </div>
            ))}
          </div>

          <div
            className="mt-6 rounded-sm border border-brand-border bg-brand-surface/40 px-4 py-4"
            data-testid="vehicle-summary-guidance"
          >
            <p className="text-[12px] leading-relaxed text-brand-text-secondary">
              Vehicle graphic pricing depends on vehicle size, coverage area, material, and whether lamination is included. Send these details and we&rsquo;ll come back with an accurate quote.
            </p>
          </div>

          <Link
            to={quoteHref}
            state={getRouteState(location)}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-brand-gold px-7 py-3.5 text-[13px] font-medium tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover"
            data-testid="vehicle-request-quote-cta"
          >
            Request Vehicle Graphics Quote <ArrowRight size={15} />
          </Link>
          <p
            className="mt-3 text-[11px] leading-relaxed text-brand-text-secondary/40"
            data-testid="vehicle-configurator-note"
          >
            Even a rough brief helps — vehicle type, rough coverage, and contact details are enough to get the quote moving.
          </p>
        </div>
      </div>
    </section>
  );
}

export { COVERAGE_OPTIONS };
